import React, { useState } from 'react';
import {
  Button,
  Tag,
  Space,
  Modal,
  Row,
  Col,
  Card,
  Statistic,
  message,
  Select,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { usePages } from '../../../hooks/useContent';
import { DataTable } from '../../../components';
import { CommonState } from '@erpgold/shared';

const statusColors: Record<number, string> = {
  [CommonState.ENABLED]: 'success',
  [CommonState.DISABLED]: 'error',
};

export const PageList: React.FC = () => {
  const navigate = useNavigate();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [statusFilter, setStatusFilter] = useState<number | undefined>(undefined);

  const {
    pages,
    loading,
    meta,
    refetch,
    deletePages,
  } = usePages();

  const handleDelete = async (id: string) => {
    try {
      await deletePages([id]);
      message.success('Page deleted successfully');
    } catch (error) {
      message.error('Failed to delete page');
    }
  };

  const handleDeleteMany = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select pages to delete');
      return;
    }
    Modal.confirm({
      title: 'Delete Pages',
      content: `Are you sure you want to delete ${selectedRowKeys.length} page(s)?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deletePages(selectedRowKeys as string[]);
          setSelectedRowKeys([]);
          message.success('Pages deleted successfully');
        } catch (error) {
          message.error('Failed to delete pages');
        }
      },
    });
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <span>{text}</span>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>{record.slug}</span>
        </Space>
      ),
    },
    {
      title: 'Sapo',
      dataIndex: 'sapo',
      key: 'sapo',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'state',
      key: 'state',
      width: 100,
      render: (state) => (
        <Tag color={statusColors[state] || 'default'}>
          {state === CommonState.ENABLED ? 'Enabled' : 'Disabled'}
        </Tag>
      ),
    },
    {
      title: 'Author',
      dataIndex: 'author',
      key: 'author',
      width: 150,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    },
  ];

  const filteredPages = statusFilter !== undefined
    ? pages.filter((p: any) => p.state === statusFilter)
    : pages;

  const filterContent = (
    <Row gutter={16}>
      <Col span={8}>
        <Select
          placeholder="Filter by status"
          allowClear
          style={{ width: '100%' }}
          value={statusFilter}
          onChange={(value) => setStatusFilter(value)}
        >
          <Select.Option value={CommonState.ENABLED}>Enabled</Select.Option>
          <Select.Option value={CommonState.DISABLED}>Disabled</Select.Option>
        </Select>
      </Col>
    </Row>
  );

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="Total Pages" value={meta.total} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active"
              value={pages.filter((p: any) => p.state === CommonState.ENABLED).length}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Inactive"
              value={pages.filter((p: any) => p.state === CommonState.DISABLED).length}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      <DataTable
        title="Pages"
        data={filteredPages}
        columns={columns}
        loading={loading}
        rowKey="page_id"
        searchable
        searchPlaceholder="Search by title..."
        onRefresh={refetch}
        filters={filterContent}
        pagination={{
          current: meta.page,
          pageSize: meta.limit,
          total: meta.total,
          onChange: () => { },
        }}
        selectionType="checkbox"
        selectedRowKeys={selectedRowKeys}
        onSelectionChange={setSelectedRowKeys}
        extraActions={
          <Space>
            {selectedRowKeys.length > 0 && (
              <Button danger icon={<DeleteOutlined />} onClick={handleDeleteMany}>
                Delete Selected ({selectedRowKeys.length})
              </Button>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/content/pages/add')}>
              Add Page
            </Button>
          </Space>
        }
        actionColumn={{
          width: 120,
          fixed: 'right',
          actions: (record) => [
            {
              key: 'edit',
              label: 'Edit',
              icon: <EditOutlined />,
              onClick: () => navigate(`/content/pages/${record._id}/edit`),
            },
            {
              key: 'delete',
              label: 'Delete',
              icon: <DeleteOutlined />,
              danger: true,
              onClick: () => {
                Modal.confirm({
                  title: 'Delete Page',
                  content: `Are you sure you want to delete "${record.title}"?`,
                  onOk: () => handleDelete(record._id),
                });
              },
            },
          ],
        }}
      />
    </div>
  );
};

export default PageList;
